import React, { FC, Fragment, ReactElement, RefObject, useRef } from 'react'
import { IconButtonWithLabel } from '../../../Atoms/IconButtonWithLabel/IconButtonWithLabel'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import IconButton, { IconButtonType } from '../../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../../React/Common/Components/Icon'
import { SidebarMenuItems } from '../../../../../NonReact/constants/ReactRouter/Header/Constants'
import { SubMenuItem } from '../../../../../NonReact/common/Types/Types'

type Props = {
    sidebarMenuItem: SubMenuItem
    isSidebarExpanded: boolean
    favourites?: SidebarMenuItems[]
    handleOnClickFavourite: (sidebarMenuItem: SidebarMenuItems) => void
    disableFavourites: boolean
}

/**
 * Component for an entry in the side menu
 *
 * @param isSidebarExpanded - Whether the sidebar is expanded or not, used to determine whether to show the label and tooltip
 * @param handleOnClickFavourite - Handler for when the favourite button is clicked, passed down from the header to update the favourites state
 * @param favourites - The current list of favourite sidebar menu items, used to determine the state of the favourite button
 * @param enabled - Whether this sidebar menu item is enabled or not, used to determine whether to render the item or an empty placeholder
 * @param icon - The name of the icon to show for this sidebar menu item, passed to the IconButtonWithLabel component
 * @param id - The ID of this sidebar menu item, used for testing and to identify which item is being favourited
 * @param title - The title of this sidebar menu item, shown as the label and tooltip for the IconButtonWithLabel component
 * @param handleClick - The handler for when this sidebar menu item is clicked, passed to the IconButtonWithLabel component
 * @param active - Whether this sidebar menu item is currently active, used to apply the active class for styling
 * @param disableFavourites - Whether the favourites functionality is disabled, used to determine whether to show the favourite button or not
 *
 */
export const SideMenuEntry: FC<Props> = ({
    isSidebarExpanded,
    handleOnClickFavourite,
    favourites,
    sidebarMenuItem: { enabled, icon, id, title, handleClick, active },
    disableFavourites
}: Props): ReactElement => {
    const infoPopperRef: RefObject<HTMLDivElement> = useRef(null)

    return enabled ? (
        <div ref={infoPopperRef} key={icon} className='sidebar-menu-item'>
            <IconButtonWithLabel
                id={`sidebar-button-${id.toLowerCase()}`}
                label={title}
                onClick={(): void => handleClick(false)}
                onMiddleClick={(): void => handleClick(true)}
                className={`sidebar-menu-item-button ${active ? 'smi-active' : EMPTY_STRING}`}
                iconName={icon}
                tooltip={isSidebarExpanded ? undefined : title}
                popperClassNames='smi-button-tooltip'
                topRightAdornment={
                    disableFavourites ? undefined : (
                        <IconButton
                            customClassName='sidebar-favourite-button'
                            iconName={favourites?.includes(id) ? IconName.FAVOURITE : IconName.FAVOURITE_OUTLINED}
                            type={IconButtonType.BUTTON}
                            onClick={(): void => handleOnClickFavourite(id)}
                        />
                    )
                }
            />
        </div>
    ) : (
        <Fragment key={icon} />
    )
}
